import { useEffect, useId, useRef, useState } from 'react';
import { PrototypeIcon } from './PrototypeIcon';
import { searchBusiness, searchPages, type GlobalSearchResult } from './global-search-api';

export function GlobalSearch({
  administrator,
  onChoose,
  autoFocus = false,
  dialog = false,
}: {
  readonly administrator: boolean;
  readonly onChoose: (result: GlobalSearchResult) => void;
  readonly autoFocus?: boolean;
  readonly dialog?: boolean;
}): React.JSX.Element {
  const [query, setQuery] = useState(''),
    [open, setOpen] = useState(false),
    [items, setItems] = useState<readonly GlobalSearchResult[]>([]),
    [loading, setLoading] = useState(false),
    [failed, setFailed] = useState(false),
    [active, setActive] = useState(-1);
  const id = useId(),
    input = useRef<HTMLInputElement>(null);
  const pages = searchPages(query, administrator);
  const results = [...pages, ...(administrator ? items : [])];
  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);
  useEffect(() => {
    if (open && active >= 0)
      document.getElementById(id + '-' + active)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active, id]);
  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setItems([]);
    setFailed(false);
    setActive(-1);
    if (!administrator || query.trim().length < 2) {
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    const timer = window.setTimeout(() => {
      void searchBusiness(query, controller.signal)
        .then((result) => {
          if (current) {
            setItems(result.items);
            setFailed(result.incomplete);
          }
        })
        .catch(() => {
          if (current) {
            setItems([]);
            setFailed(true);
          }
        })
        .finally(() => {
          if (current) setLoading(false);
        });
    }, 220);
    return () => {
      current = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query, administrator]);
  function choose(result: GlobalSearchResult): void {
    setOpen(false);
    setQuery('');
    setItems([]);
    onChoose(result);
  }
  return (
    <div
      className={
        'app-search tosho-search ' + (dialog ? 'tosho-search--dialog' : 'app-search--inline')
      }
      role="search"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <PrototypeIcon className="app-search__icon" name="search" />
      <input
        ref={input}
        className="app-search__input"
        type="search"
        role="combobox"
        value={query}
        maxLength={120}
        aria-label="Rechercher partout dans Tosho"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={id}
        aria-activedescendant={open && results[active] ? id + '-' + active : undefined}
        placeholder="Rechercher partout dans Tosho..."
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setItems([]);
          setActive(-1);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            return;
          }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            setActive((index) =>
              results.length
                ? index < 0
                  ? event.key === 'ArrowDown'
                    ? 0
                    : results.length - 1
                  : (index + (event.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length
                : -1,
            );
          } else if (event.key === 'Enter' && open && results[Math.max(0, active)]) {
            event.preventDefault();
            choose(results[Math.max(0, active)]!);
          }
        }}
      />
      {!dialog && <kbd aria-hidden="true">⌘ K</kbd>}
      {open && (
        <div className="tosho-search__popover">
          <ul id={id} role="listbox" aria-label="Suggestions de recherche">
            {results.map((result, index) => (
              <li
                role="option"
                id={id + '-' + index}
                key={result.key}
                aria-selected={index === active}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => setActive(index)}
                  onClick={() => choose(result)}
                >
                  <span className="tosho-search__result-icon">
                    <PrototypeIcon name={result.icon} />
                  </span>
                  <span>
                    <strong>{result.label}</strong>
                    <small>{result.detail}</small>
                  </span>
                  <PrototypeIcon name="arrow-right" className="tosho-search__arrow" />
                </button>
              </li>
            ))}
          </ul>
          {loading && <p role="status">Recherche en cours…</p>}
          {!loading && !failed && !results.length && (
            <p role="status">Aucun résultat pour « {query} ».</p>
          )}
          {failed && (
            <p role="status">
              Certains résultats ne sont pas disponibles. Réessayez dans un instant.
            </p>
          )}
          {!!results.length && (
            <footer>
              <span>↑ ↓ pour parcourir</span>
              <span>Entrée pour ouvrir · Échap pour fermer</span>
            </footer>
          )}
        </div>
      )}
    </div>
  );
}
